import startCase from 'lodash/startCase';
import moment from 'moment';

const STORED_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

export interface MigratedRequirement {
    type: string;
    name: string;
    formattedDate: string;
}

// The labels a relying party has given its requirement types, where it has given any.
export type RequirementLabels = Record<string, { defaultLabel?: string | null } | undefined>;

// The relying party's own label for a requirement, as the rest of the portal shows it, with the
// type's name tidied up as the fallback.
export function getRequirementDisplayName(requirementType: string, labels?: RequirementLabels): string {
    return labels?.[requirementType]?.defaultLabel || startCase(requirementType.toLowerCase().replace(/_/g, ' '));
}

function listRequirements(
    exceptions: Record<string, string | null | undefined> | null | undefined,
    requirementTypes: string[],
    passed: boolean,
    labels?: RequirementLabels,
): MigratedRequirement[] {
    const listed: MigratedRequirement[] = [];

    for (const type of requirementTypes) {
        const date = moment(exceptions?.[type], STORED_DATE_FORMAT, true);

        // A half-typed date is neither migrated nor expired.
        if (date.isValid() && date.isBefore(moment(), 'day') === passed) {
            listed.push({
                type,
                name: getRequirementDisplayName(type, labels),
                formattedDate: date.format(DISPLAY_DATE_FORMAT),
            });
        }
    }

    return listed;
}

// Requirements with a complete date of today or later, in the order of `requirementTypes`.
// These are the ones saving marks compliant.
export function getMigratedRequirements(
    exceptions: Record<string, string | null | undefined> | null | undefined,
    requirementTypes: string[],
    labels?: RequirementLabels,
): MigratedRequirement[] {
    return listRequirements(exceptions, requirementTypes, false, labels);
}

// Requirements whose date has already passed. Saving still records the date, but the entity is
// asked for a document straight away, so they are not counted as migrated.
export function getExpiredRequirements(
    exceptions: Record<string, string | null | undefined> | null | undefined,
    requirementTypes: string[],
    labels?: RequirementLabels,
): MigratedRequirement[] {
    return listRequirements(exceptions, requirementTypes, true, labels);
}
